import { useState, useEffect } from "react";
import { useMutation } from "@apollo/client";

import { useUser } from "MVVM/Hooks/useUser";
import { usePortal } from "MVVM/Hooks/usePortal";
import { UnifiedUpgradeModal } from "MVVM/Components/Modals/UnifiedUpgradeModal";
import { USER_UPDATE_AUTO_SAVE } from "MVVM/GraphQL/user";
import { analytics } from "MVVM/Analytics";

const AUTO_SAVE_ELIGIBLE_TIERS = [
  "PRO",
  "PRO_PLUS",
  "CREATOR_PLAT",
  "INTERNAL",
];

export const useAutoSave = () => {
  const { allstarUser, setAllstarUser } = useUser();
  const userType = allstarUser?.user?.userGroup?.type;
  const isEligible = Boolean(
    userType && AUTO_SAVE_ELIGIBLE_TIERS.includes(userType),
  );
  const serverValue = allstarUser?.user?.autoSaveClips ?? false;
  // null/unset = save everything, matching the 30 ("Everything") preset.
  const serverThreshold = allstarUser?.user?.autoSaveThreshold ?? 30;
  const [optimisticValue, setOptimisticValue] = useState(serverValue);
  const [optimisticThreshold, setOptimisticThreshold] =
    useState(serverThreshold);

  const {
    portalOpen: upsellOpen,
    setPortalOpen: setUpsellOpen,
    Portal: UpsellPortal,
  } = usePortal(
    <UnifiedUpgradeModal
      featureName="Auto-save your Clips"
      upgradeAction="Auto-save your Clips"
      requiredTierKey="PRO"
      analyticsSource="Clip Settings Modal"
      onClose={() => setUpsellOpen(false)}
    />,
  );

  useEffect(() => {
    setOptimisticValue(serverValue);
  }, [serverValue]);

  useEffect(() => {
    setOptimisticThreshold(serverThreshold);
  }, [serverThreshold]);

  const [updateUser, { loading }] = useMutation(USER_UPDATE_AUTO_SAVE, {
    onCompleted: (data) => {
      const errors = data?.userUpdate?.errors;
      if (errors?.length) {
        setOptimisticValue(serverValue);
        setOptimisticThreshold(serverThreshold);
        analytics.track("Auto-Save Toggle - Error", {
          source: "clip-settings",
          error: errors[0].message,
        });
        return;
      }

      const viewer = data?.userUpdate?.viewer;
      const newValue = viewer?.autoSaveClips ?? !serverValue;
      const newThreshold = viewer?.autoSaveThreshold ?? serverThreshold;
      setAllstarUser((prev: any) => ({
        ...prev,
        user: {
          ...prev.user,
          autoSaveClips: newValue,
          autoSaveThreshold: newThreshold,
        },
      }));
      analytics.track("Auto-Save Toggle - Success", {
        source: "clip-settings",
        enabled: newValue,
        threshold: newThreshold,
      });
    },
    onError: (error) => {
      setOptimisticValue(serverValue);
      setOptimisticThreshold(serverThreshold);
      analytics.track("Auto-Save Toggle - Error", {
        source: "clip-settings",
        error: error.message,
      });
    },
  });

  const handleToggle = () => {
    if (loading) return;

    if (!isEligible) {
      analytics.track("Auto-Save Toggle - Upsell Clicked", {
        source: "clip-settings",
        userTier: userType,
      });
      setUpsellOpen(true);
      return;
    }

    const newValue = !optimisticValue;
    setOptimisticValue(newValue);
    analytics.track("Auto-Save Toggle - Toggled", {
      source: "clip-settings",
      enabled: newValue,
      previousValue: optimisticValue,
    });

    updateUser({
      variables: { input: { autoSaveClips: newValue } },
    });
  };

  const handleThresholdSelect = (threshold: number) => {
    if (loading || !isEligible || threshold === optimisticThreshold) return;

    setOptimisticThreshold(threshold);
    analytics.track("Auto-Save Threshold - Changed", {
      source: "clip-settings",
      threshold,
      previousThreshold: optimisticThreshold,
    });

    updateUser({
      variables: { input: { autoSaveThreshold: threshold } },
    });
  };

  return {
    optimisticValue,
    optimisticThreshold,
    loading,
    isEligible,
    handleToggle,
    handleThresholdSelect,
    upsellOpen,
    UpsellPortal,
  };
};
